import { useState, useEffect, useMemo } from 'react';
import { Dropdown, Avatar, Drawer, Tooltip } from 'antd';
import {
  TableOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  CalendarOutlined,
  InboxOutlined,
  MenuOutlined,
  CloseOutlined,
  BankOutlined,
  DollarOutlined,
  FileTextOutlined,
  WalletOutlined,
  DeleteOutlined,
  LineChartOutlined,
  SunOutlined,
  MoonOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { CUSTOMER_NAME } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { AnnouncementBanner } from './AnnouncementBanner';

dayjs.locale('tr');

interface LayoutProps {
  children: React.ReactNode;
}

// Menu items with their corresponding report keys for permission filtering
const allMenuItems = [
  {
    key: '/',
    reportKey: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Genel Bakış',
  },
  {
    key: '/ciro-takip',
    reportKey: 'ciro-takip',
    icon: <TableOutlined />,
    label: 'Online Ciro Takip',
  },
  {
    key: '/kasa-raporu',
    reportKey: 'kasa-raporu',
    icon: <WalletOutlined />,
    label: 'Kasa Raporu',
  },
  {
    key: '/bankalar',
    reportKey: 'bankalar',
    icon: <BankOutlined />,
    label: 'Bankalar',
  },
  {
    key: '/borclular',
    reportKey: 'borclular',
    icon: <DollarOutlined />,
    label: 'Borçlular',
  },
  {
    key: '/alacaklilar',
    reportKey: 'alacaklilar',
    icon: <DollarOutlined />,
    label: 'Alacaklılar',
  },
  {
    key: '/verilen-cekler',
    reportKey: 'verilen-cekler',
    icon: <FileTextOutlined />,
    label: 'Verilen Çekler',
  },
  {
    key: '/alinan-cekler',
    reportKey: 'alinan-cekler',
    icon: <FileTextOutlined />,
    label: 'Alınan Çekler',
  },
  {
    key: '/fire-raporu',
    reportKey: 'fire-raporu',
    icon: <DeleteOutlined />,
    label: 'Fire Raporu',
  },
  {
    key: '/giderler',
    reportKey: 'giderler-raporu',
    icon: <DollarOutlined />,
    label: 'Giderler',
  },
  {
    key: '/stok-fifo',
    reportKey: 'stok-fifo',
    icon: <InboxOutlined />,
    label: 'Stok FIFO Raporu',
  },
  {
    key: '/stok-maliyet',
    reportKey: 'stok-maliyet',
    icon: <InboxOutlined />,
    label: 'Stok Maliyet Raporu',
  },
  {
    key: '/satis-karlilik',
    reportKey: 'satis-karlilik',
    icon: <LineChartOutlined />,
    label: 'Satış Karlılık',
  },
];

// Hook to detect mobile/tablet breakpoints
function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const checkBreakpoint = () => {
      if (window.innerWidth < 768) {
        setBreakpoint('mobile');
      } else if (window.innerWidth < 1024) {
        setBreakpoint('tablet');
      } else {
        setBreakpoint('desktop');
      }
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, []);

  return breakpoint;
}

export function Layout({ children }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, announcements, dismissAnnouncement, hasReportAccess } = useAuth();
  const breakpoint = useBreakpoint();
  const { mode, toggleTheme } = useTheme();

  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    return allMenuItems.filter(item => hasReportAccess(item.reportKey));
  }, [hasReportAccess]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const isMobile = breakpoint === 'mobile';
  const isTablet = breakpoint === 'tablet';

  // Auto-collapse on tablet
  useEffect(() => {
    if (isTablet) {
      setCollapsed(true);
    }
  }, [isTablet]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600, color: '#2d2d2d', fontSize: 14 }}>{user?.name}</div>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>{user?.email}</div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ color: '#dc2626' }} />,
      label: <span style={{ color: '#dc2626' }}>Çıkış Yap</span>,
      onClick: handleLogout,
    },
  ];

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={styles.logoContainer}>
        {(!collapsed || isMobile) ? (
          <div style={styles.logoText}>
            <span style={styles.logoName}>{CUSTOMER_NAME}</span>
          </div>
        ) : (
          <div style={styles.logoIconCollapsed}>BP</div>
        )}
        {isMobile && (
          <button
            style={styles.mobileCloseButton}
            onClick={() => setMobileMenuOpen(false)}
          >
            <CloseOutlined />
          </button>
        )}
      </div>

      {/* Menu */}
      <nav style={styles.nav}>
        {menuItems.map((item) => (
          <div
            key={item.key}
            className={`sidebar-menu-item ${location.pathname === item.key ? 'active' : ''}`}
            onClick={() => handleNavigate(item.key)}
          >
            {item.icon}
            {(!collapsed || isMobile) && <span>{item.label}</span>}
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      <div style={styles.sidebarFooter}>
        {(!collapsed || isMobile) ? (
          <div style={styles.footerContent}>
            <span style={styles.footerTextLeft}>NewtonTech</span>
            <img
              src="/newtontech.png"
              alt="NewtonTech"
              style={{ height: 36, objectFit: 'contain' }}
            />
            <img
              src="/raporgor-logo.png"
              alt=""
              style={{ height: 20, width: 20, objectFit: 'contain', marginLeft: 4 }}
            />
            <span style={styles.footerText}>RaporGör</span>
          </div>
        ) : (
          <div style={styles.footerContentCollapsed}>
            <img
              src="/newtontech.png"
              alt="NewtonTech"
              style={{ height: 24, objectFit: 'contain' }}
            />
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');

        * {
          font-family: 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .sidebar-menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          margin: 4px 12px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          color: var(--color-text-muted);
          font-size: 14px;
          font-weight: 600;
          min-height: 48px;
        }

        .sidebar-menu-item:hover {
          background: var(--color-accent-bg);
          color: var(--color-primary);
        }

        .sidebar-menu-item.active {
          background: var(--color-primary);
          color: var(--color-text-inverse);
          box-shadow: 0 2px 8px var(--shadow-color-strong);
        }

        .sidebar-menu-item .anticon {
          font-size: 18px;
        }

        .header-toggle:hover {
          background: var(--color-hover-bg) !important;
          color: var(--color-primary) !important;
        }

        .theme-toggle-btn {
          width: 44px;
          height: 44px;
          border: none;
          background: var(--color-header-bg);
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-secondary);
          transition: all 0.2s ease;
        }

        .theme-toggle-btn:hover {
          background: var(--color-hover-bg);
          color: var(--color-primary);
        }

        .theme-dark .theme-toggle-btn {
          background: var(--color-header-bg);
        }

        .user-dropdown-trigger:hover {
          background: var(--color-hover-bg);
        }

        .ant-dropdown-menu {
          padding: 8px !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12) !important;
        }

        .ant-dropdown-menu-item {
          border-radius: 8px !important;
          padding: 8px 12px !important;
        }

        /* Mobile drawer styles */
        .mobile-drawer .ant-drawer-body {
          padding: 0 !important;
          background: var(--color-sidebar-bg) !important;
        }

        .mobile-drawer .ant-drawer-header {
          display: none !important;
        }

        .mobile-drawer .ant-drawer-content {
          background: var(--color-sidebar-bg) !important;
        }

        /* Responsive adjustments */
        @media (max-width: 767px) {
          .desktop-sidebar {
            display: none !important;
          }

          .mobile-header-logo {
            display: flex !important;
          }
        }

        @media (min-width: 768px) {
          .mobile-header-logo {
            display: none !important;
          }
        }

        /* Mobile scroll fix */
        html, body, #root {
          height: 100%;
          overflow: hidden;
        }

        .page-scroll-container {
          -webkit-overflow-scrolling: touch;
          overflow-y: auto;
          overflow-x: hidden;
        }
      `}</style>

      <div style={styles.container}>
        {/* Desktop/Tablet Sidebar */}
        {!isMobile && (
          <aside
            className="desktop-sidebar"
            style={{
              ...styles.sidebar,
              width: collapsed ? 72 : 260,
            }}
          >
            <SidebarContent />
          </aside>
        )}

        {/* Mobile Drawer */}
        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={280}
          className="mobile-drawer"
          closable={false}
        >
          <div style={{ ...styles.sidebar, width: '100%', position: 'relative' }}>
            <SidebarContent />
          </div>
        </Drawer>

        {/* Main Content */}
        <div style={{
          ...styles.main,
          marginLeft: isMobile ? 0 : (collapsed ? 72 : 260),
        }}>
          {/* Header */}
          <header style={{
            ...styles.header,
            padding: isMobile ? '0 16px' : '0 24px',
          }}>
            <div style={styles.headerLeft}>
              {/* Mobile menu toggle */}
              {isMobile ? (
                <button
                  className="header-toggle"
                  style={styles.toggleButton}
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MenuOutlined />
                </button>
              ) : (
                <button
                  className="header-toggle"
                  style={styles.toggleButton}
                  onClick={() => setCollapsed(!collapsed)}
                >
                  {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                </button>
              )}

              {/* Mobile logo */}
              <div className="mobile-header-logo" style={styles.mobileHeaderLogo}>
                <span style={styles.mobileLogoText}>{CUSTOMER_NAME}</span>
              </div>

              {/* Desktop breadcrumb */}
              {!isMobile && (
                <div style={styles.breadcrumb}>
                  <span style={styles.breadcrumbText}>
                    {menuItems.find(i => i.key === location.pathname)?.label || 'Dashboard'}
                  </span>
                </div>
              )}
            </div>

            <div style={styles.headerRight}>
              {/* Theme Toggle */}
              <Tooltip title={mode === 'light' ? 'Koyu Tema' : 'Açık Tema'}>
                <button
                  className="theme-toggle-btn"
                  style={styles.themeToggle}
                  onClick={toggleTheme}
                >
                  {mode === 'light' ? (
                    <MoonOutlined style={{ fontSize: 18 }} />
                  ) : (
                    <SunOutlined style={{ fontSize: 18, color: '#fbbf24' }} />
                  )}
                </button>
              </Tooltip>

              {/* Date/Time - Desktop only */}
              {!isMobile && (
                <div style={styles.dateTime}>
                  <CalendarOutlined style={{ marginRight: 6, color: 'var(--color-primary)' }} />
                  <span>{currentTime.format('D MMMM, HH:mm')}</span>
                </div>
              )}
              <Dropdown
                menu={{ items: userMenuItems }}
                trigger={['click']}
                placement="bottomRight"
              >
                <div className="user-dropdown-trigger" style={{
                  ...styles.userTrigger,
                  padding: isMobile ? '8px' : '8px 12px',
                }}>
                  <Avatar
                    size={isMobile ? 32 : 36}
                    style={styles.avatar}
                    icon={<UserOutlined />}
                  />
                  {!isMobile && (
                    <>
                      <div style={styles.userInfo}>
                        <span style={styles.userName}>{user?.name}</span>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </>
                  )}
                </div>
              </Dropdown>
            </div>
          </header>

          {/* Page Content */}
          <main style={{
            ...styles.content,
            padding: isMobile ? 16 : 24,
          }}>
            <div style={styles.contentInner}>
              {/* Announcements */}
              {announcements.length > 0 && (
                <AnnouncementBanner
                  announcements={announcements}
                  onDismiss={dismissAnnouncement}
                />
              )}
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    height: '100vh',
    background: 'var(--color-page-bg)',
    overflow: 'hidden',
  },
  sidebar: {
    background: 'var(--color-sidebar-bg)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 100,
    boxShadow: '1px 0 0 var(--color-border)',
    borderRight: '1px solid var(--color-border)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '20px 16px',
    borderBottom: '1px solid var(--color-border)',
  },
  logoIconCollapsed: {
    color: 'var(--color-primary)',
    fontSize: 16,
    fontWeight: 800,
    background: 'var(--color-accent-bg)',
    borderRadius: 8,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  logoName: {
    color: 'var(--color-primary)',
    fontSize: 18,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    letterSpacing: '-0.02em',
  },
  mobileCloseButton: {
    background: 'var(--color-header-bg)',
    border: 'none',
    borderRadius: 8,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-muted)',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  nav: {
    flex: 1,
    padding: '16px 0',
    overflowY: 'auto',
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid var(--color-border)',
  },
  footerContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-disabled)',
    fontSize: 12,
    gap: 8,
  },
  footerContentCollapsed: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerTextLeft: {
    fontWeight: 700,
    fontSize: 11,
    color: 'var(--color-text-muted)',
  },
  footerText: {
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: 'var(--color-text-muted)',
  },
  main: {
    flex: 1,
    transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  header: {
    height: 72,
    background: 'var(--color-card-bg)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    boxShadow: '0 1px 3px var(--shadow-color)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  toggleButton: {
    width: 44,
    height: 44,
    border: 'none',
    background: 'transparent',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    color: 'var(--color-text-secondary)',
    transition: 'all 0.2s ease',
  },
  themeToggle: {
    width: 44,
    height: 44,
    border: 'none',
    background: 'var(--color-header-bg)',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-secondary)',
    transition: 'all 0.2s ease',
  },
  mobileHeaderLogo: {
    display: 'none',
    alignItems: 'center',
    gap: 8,
  },
  mobileLogoText: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--color-primary)',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.02em',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  dateTime: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    padding: '8px 12px',
    background: 'var(--color-header-bg)',
    borderRadius: 10,
  },
  userTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  avatar: {
    background: 'var(--color-primary)',
    border: '2px solid var(--color-primary-light)',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  userRole: {
    fontSize: 12,
    color: 'var(--color-text-disabled)',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  contentInner: {
    maxWidth: 1400,
    margin: '0 auto',
  },
};
